import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { toast } from 'react-hot-toast';

export default function EmailPreferences() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    marketing_emails: true,
    subscription_lifecycle_emails: true,
    expert_marketplace_emails: true,
    report_notifications: true,
    contract_notifications: true,
    payment_notifications: true,
    unsubscribed_all: false
  });

  useEffect(() => {
    loadPreferences();
  }, [user]);

  async function loadPreferences() {
    try {
      const { data, error } = await supabase
        ?.from('email_preferences')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.single();

      if (error && error?.code !== 'PGRST116') throw error;

      if (data) {
        setPreferences({
          marketing_emails: data?.marketing_emails,
          subscription_lifecycle_emails: data?.subscription_lifecycle_emails,
          expert_marketplace_emails: data?.expert_marketplace_emails,
          report_notifications: data?.report_notifications,
          contract_notifications: data?.contract_notifications,
          payment_notifications: data?.payment_notifications,
          unsubscribed_all: data?.unsubscribed_all
        });
      }
    } catch (error) {
      console.error('Error loading email preferences:', error);
      toast?.error('Failed to load email preferences');
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      const { error } = await supabase
        ?.from('email_preferences')
        ?.upsert({
          user_id: user?.id,
          ...preferences,
          updated_at: new Date()?.toISOString()
        });

      if (error) throw error;

      toast?.success('Email preferences saved successfully');
    } catch (error) {
      console.error('Error saving email preferences:', error);
      toast?.error('Failed to save email preferences');
    } finally {
      setSaving(false);
    }
  }

  function handleChange(field, value) {
    setPreferences(prev => ({
      ...prev,
      [field]: value
    }));
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-md p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Email Preferences</h1>
        <p className="text-gray-600 mb-8">Manage your email notification settings</p>

        {preferences?.unsubscribed_all && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-yellow-800 font-medium">You are currently unsubscribed from all emails.</p>
            <p className="text-yellow-700 text-sm mt-1">You will only receive critical transactional emails (verification, 2FA).</p>
          </div>
        )}

        <div className="space-y-6">
          {/* Global Unsubscribe */}
          <div className="border-b border-gray-200 pb-6">
            <div className="flex items-start space-x-3">
              <Checkbox
                checked={preferences?.unsubscribed_all}
                onChange={(e) => handleChange('unsubscribed_all', e?.target?.checked)}
                className="mt-1"
              />
              <div>
                <label className="font-semibold text-gray-900 block">Unsubscribe from all emails</label>
                <p className="text-sm text-gray-600 mt-1">
                  You'll only receive critical transactional emails (verification, 2FA codes)
                </p>
              </div>
            </div>
          </div>

          {/* Individual Preferences */}
          <div className="space-y-4 opacity-${preferences?.unsubscribed_all ? '50' : '100'}">
            <h3 className="font-semibold text-gray-900 text-lg">Email Categories</h3>

            <div className="flex items-start space-x-3">
              <Checkbox
                checked={preferences?.report_notifications}
                onChange={(e) => handleChange('report_notifications', e?.target?.checked)}
                disabled={preferences?.unsubscribed_all}
                className="mt-1"
              />
              <div>
                <label className="font-medium text-gray-900 block">Report Notifications</label>
                <p className="text-sm text-gray-600 mt-1">
                  Get notified when your forensic analysis reports are ready
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox
                checked={preferences?.contract_notifications}
                onChange={(e) => handleChange('contract_notifications', e?.target?.checked)}
                disabled={preferences?.unsubscribed_all}
                className="mt-1"
              />
              <div>
                <label className="font-medium text-gray-900 block">Contract Notifications</label>
                <p className="text-sm text-gray-600 mt-1">
                  Updates on expert contracts, offers, acceptances, and completions
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox
                checked={preferences?.payment_notifications}
                onChange={(e) => handleChange('payment_notifications', e?.target?.checked)}
                disabled={preferences?.unsubscribed_all}
                className="mt-1"
              />
              <div>
                <label className="font-medium text-gray-900 block">Payment Notifications</label>
                <p className="text-sm text-gray-600 mt-1">
                  Receipts, payment confirmations, and failed payment alerts
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox
                checked={preferences?.subscription_lifecycle_emails}
                onChange={(e) => handleChange('subscription_lifecycle_emails', e?.target?.checked)}
                disabled={preferences?.unsubscribed_all}
                className="mt-1"
              />
              <div>
                <label className="font-medium text-gray-900 block">Subscription Lifecycle</label>
                <p className="text-sm text-gray-600 mt-1">
                  Welcome emails, renewal reminders, cancellation confirmations
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox
                checked={preferences?.expert_marketplace_emails}
                onChange={(e) => handleChange('expert_marketplace_emails', e?.target?.checked)}
                disabled={preferences?.unsubscribed_all}
                className="mt-1"
              />
              <div>
                <label className="font-medium text-gray-900 block">Expert Marketplace</label>
                <p className="text-sm text-gray-600 mt-1">
                  Job offers, application updates, payout notifications (for experts)
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Checkbox
                checked={preferences?.marketing_emails}
                onChange={(e) => handleChange('marketing_emails', e?.target?.checked)}
                disabled={preferences?.unsubscribed_all}
                className="mt-1"
              />
              <div>
                <label className="font-medium text-gray-900 block">Marketing Emails</label>
                <p className="text-sm text-gray-600 mt-1">
                  Product updates, feature announcements, and promotional offers
                </p>
              </div>
            </div>
          </div>

          {/* Transactional Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
            <p className="text-blue-900 font-medium text-sm">Note about transactional emails</p>
            <p className="text-blue-800 text-sm mt-1">
              You will always receive critical transactional emails such as email verification and 2FA codes, regardless of your preferences.
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={loadPreferences}
            disabled={saving}
          >
            Reset
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Preferences'}
          </Button>
        </div>
      </div>
    </div>
  );
}
